/**
 * Quick or thorough: which planner answers the next message. It sits beside
 * the chat box because every message goes to the planner it names, the first
 * whole-site build included (that one starts from the interview answers, before
 * there is a draft to rebuild). The choice is remembered in this browser.
 */

export type BuildTier = 'quick' | 'thorough';

const STORAGE_KEY = 'structure-planner-tier';

/** The remembered choice; quick when there is none or storage is blocked. */
export function loadBuildTier(): BuildTier {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'thorough' ? 'thorough' : 'quick';
  } catch {
    return 'quick';
  }
}

export function saveBuildTier(tier: BuildTier): void {
  try {
    localStorage.setItem(STORAGE_KEY, tier);
  } catch {
    // Private window or blocked storage: the choice lasts until reload.
  }
}

const OPTIONS: { tier: BuildTier; label: string; title: string }[] = [
  {
    tier: 'quick',
    label: 'Quick',
    title: 'The quick planner: a whole-site build takes several minutes.',
  },
  {
    tier: 'thorough',
    label: 'Thorough',
    title:
      'The thorough planner: slower, and searches the research room for every sub-section before writing keywords of its own.',
  },
];

export default function BuildTierToggle({
  value,
  onChange,
  disabled,
}: {
  value: BuildTier;
  onChange: (tier: BuildTier) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Planner"
      className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"
    >
      <span>Planner:</span>
      <span className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
        {OPTIONS.map((o) => (
          <button
            key={o.tier}
            type="button"
            role="radio"
            aria-checked={value === o.tier}
            title={o.title}
            disabled={disabled}
            onClick={() => onChange(o.tier)}
            className={`px-2 py-0.5 disabled:opacity-50 ${
              value === o.tier
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {o.label}
          </button>
        ))}
      </span>
    </div>
  );
}
